import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useEffect, useState, type ReactNode } from "react";
import { PhasePill, type BeaconPhase } from "./primitives/PhasePill";
import { useAuth, usePhase } from "../state/store";

/**
 * Beacon AppFrame — durchgehender Header (56px) + Footer (48px) über alle
 * Routen pro Skin. Phase-Pill links, Clock rechts, Phase-Verbose unten.
 *
 * skin="presenter" → dunkles Beamer-Bühnenbild
 * skin="light"     → helles Mobile/Showcase/Admin
 *
 * Setzt zur Laufzeit `html.skin-presenter` bzw. `html.skin-light` als
 * Klassen-Hook für globale CSS-Overrides (siehe tokens-beacon.css).
 */

interface AppFrameProps {
  skin: "presenter" | "light";
  children: ReactNode;
  /** Optional Breadcrumb-Slot in der Header-Mitte. */
  breadcrumb?: ReactNode;
  /** Optional Phase-Verbose-Slot in der Footer-Mitte. Wenn null,
   *  erzeugen wir aus Workshop-State eine Default-Anzeige. */
  phaseFooter?: ReactNode;
  /** Tag-Stempel rechts im Header. Nur das zweitägige Vertriebs-Offsite nutzt ihn. */
  tagLabel?: "TAG 1" | "TAG 2";
  /** Wenn true, wird der Header NICHT gerendert (z.B. Login-Vollbild). */
  hideHeader?: boolean;
  /** Wenn true, wird der Footer NICHT gerendert. */
  hideFooter?: boolean;
}

const HEADER_HEIGHT = 56;
const FOOTER_HEIGHT = 48;

export function AppFrame({
  skin,
  children,
  breadcrumb,
  phaseFooter,
  tagLabel = "TAG 1",
  hideHeader,
  hideFooter,
}: AppFrameProps) {
  const { wordmark } = useOffsiteConfig();
  const auth = useAuth();
  const phase = usePhase() as BeaconPhase;

  // Skin auf <html> hängen + auf Phase-Wechsel kurz crossfaden.
  const [fading, setFading] = useState(false);
  useEffect(() => {
    document.documentElement.classList.add(`skin-${skin}`);
    document.documentElement.classList.remove(
      skin === "presenter" ? "skin-light" : "skin-presenter",
    );
    return () => {
      // Skin-Klasse beim Unmount entfernen, damit Login-Page neu setzen kann.
      // (Kein removeAll, weil ein anderer AppFrame ggf. übernimmt.)
    };
  }, [skin]);

  useEffect(() => {
    setFading(true);
    const t = window.setTimeout(() => setFading(false), 320);
    return () => window.clearTimeout(t);
  }, [phase]);

  return (
    <div
      className={`app-frame app-frame--${skin}`}
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateRows: `${hideHeader ? "0" : `${HEADER_HEIGHT}px`} 1fr ${hideFooter ? "0" : `${FOOTER_HEIGHT}px`}`,
        background: skin === "presenter" ? "var(--ink-800)" : "var(--paper-50)",
        color: skin === "presenter" ? "var(--svo-weiss, #fff)" : "var(--paper-900)",
      }}
    >
      {!hideHeader && (
        <header
          className="app-frame-header"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--s-5)",
            padding: "0 var(--s-6)",
            borderBottom:
              skin === "presenter"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid var(--paper-200)",
            opacity: fading ? 0.6 : 1,
            transition: "opacity 320ms ease-out",
          }}
        >
          <span
            className="mono app-frame-brand"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "var(--tracking-micro)",
              textTransform: "uppercase",
              color: skin === "presenter" ? "var(--ink-200)" : "var(--paper-500)",
              whiteSpace: "nowrap",
            }}
          >
            {wordmark ?? "SVO · OFFSITE 2026"}
          </span>
          {breadcrumb && (
            <span
              className="mono app-frame-breadcrumb"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "var(--tracking-meta)",
                textTransform: "uppercase",
                color: skin === "presenter" ? "var(--ink-300)" : "var(--paper-400)",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {breadcrumb}
            </span>
          )}
          {!breadcrumb && <span style={{ flex: 1 }} />}
          <PhasePill phase={phase} />
          <Clock skin={skin} tagLabel={tagLabel} />
          {auth.role === "reviewer" && <ReviewerChip />}
        </header>
      )}

      <main
        className="app-frame-main"
        style={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: fading ? 0.85 : 1,
          transition: "opacity 320ms ease-out",
        }}
      >
        {children}
      </main>

      {!hideFooter && (
        <footer
          className="app-frame-footer"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: "var(--s-5)",
            padding: "0 var(--s-6)",
            borderTop:
              skin === "presenter"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid var(--paper-200)",
          }}
        >
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: skin === "presenter" ? "var(--ink-400)" : "var(--paper-400)",
            }}
          >
            ← / → SLIDES · F FULLSCREEN · R RESET · ⌘K COMMAND
          </span>
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: skin === "presenter" ? "var(--ink-200)" : "var(--paper-500)",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            {phaseFooter ?? <DefaultPhaseFooter phase={phase} />}
          </span>
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: skin === "presenter" ? "var(--ink-400)" : "var(--paper-400)",
              textAlign: "right",
            }}
          >
            RESET ↻
          </span>
        </footer>
      )}
    </div>
  );
}

function Clock({ skin, tagLabel }: { skin: "presenter" | "light"; tagLabel: string }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return (
    <span
      className="mono"
      style={{
        fontFamily: "var(--font-mono)",
        fontVariantNumeric: "tabular-nums",
        fontSize: 13,
        color: skin === "presenter" ? "var(--ink-200)" : "var(--paper-500)",
        textTransform: "uppercase",
        letterSpacing: "var(--tracking-meta)",
        whiteSpace: "nowrap",
      }}
    >
      {hh}:{mm} · {tagLabel}
    </span>
  );
}

function ReviewerChip() {
  return (
    <span
      className="mono"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "var(--tracking-meta)",
        textTransform: "uppercase",
        color: "var(--signal-warn)",
        border: "1px solid var(--signal-warn)",
        padding: "2px 8px",
        borderRadius: "var(--r-pill)",
      }}
    >
      READ-ONLY
    </span>
  );
}

function DefaultPhaseFooter({ phase }: { phase: BeaconPhase }) {
  // Generic Default — Pages liefern besseren Sub-Info-Text per phaseFooter.
  const map: Record<BeaconPhase, string> = {
    waiting: "PHASE · WAITING",
    topics: "PHASE · THEMEN",
    voting: "PHASE · VOTING · LIVE",
    "winner-reveal": "PHASE · WINNER",
    grouping: "PHASE · TEAM-ZUTEILUNG",
    working: "PHASE · WORKING",
    einfuehrung: "PHASE · EINFÜHRUNG",
    abschluss: "PHASE · ABSCHLUSS",
    submitted: "PHASE · SUBMITTED",
    evaluating: "PHASE · EVALUATING · KI",
    evaluated: "PHASE · EVALUATED",
    showcase: "PHASE · SHOWCASE",
    overnight: "PHASE · OVERNIGHT · LÄUFT",
    // Kein „TAG 2" — das war das zweitägige Vertriebs-Offsite, und selbst dort
    // liegt der Reveal inzwischen am Ende von Tag 1.
    reveal: "PHASE · REVEAL",
    done: "PHASE · DONE",
  };
  return <>{map[phase] ?? "PHASE"}</>;
}
